const API_BASE = '/api';

interface ApiResponse<T> {
  success: boolean;
  data: T | null;
  message: string;
  timestamp: string;
}

class MilkApiClient {
  private token: string | null = null;

  private async readResponseBody(response: Response): Promise<string> {
    if (typeof response.text === 'function') {
      return response.text();
    }

    if (typeof response.json === 'function') {
      const jsonData = await response.json();
      return JSON.stringify(jsonData);
    }

    return '';
  }

  private async downloadFile(url: string, fileName: string, contentType: string) {
    const token = this.getToken();
    const response = await fetch(`${API_BASE}${url}`, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    });
    if (!response.ok) throw new Error('Download failed');
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(blobUrl);
  }

  private parseApiResponse<T>(rawText: string, fallbackMessage: string): ApiResponse<T> {
    if (!rawText) {
      return {
        success: false,
        data: null,
        message: fallbackMessage,
        timestamp: new Date().toISOString(),
      };
    }

    try {
      return JSON.parse(rawText) as ApiResponse<T>;
    } catch {
      return {
        success: false,
        data: null,
        message: rawText.slice(0, 400) || fallbackMessage,
        timestamp: new Date().toISOString(),
      };
    }
  }

  setToken(token: string) {
    this.token = token;
    localStorage.setItem('milk_token', token);
  }

  getToken(): string | null {
    if (!this.token) {
      this.token = localStorage.getItem('milk_token');
    }
    return this.token;
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('milk_token');
    localStorage.removeItem('milk_user');
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    const token = this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers: { ...headers, ...options.headers as Record<string, string> },
    });

    const raw = await this.readResponseBody(response);
    const data = this.parseApiResponse<T>(raw, `Request failed (${response.status})`);

    if (!response.ok && response.status === 401) {
      this.clearToken();
      window.location.href = '/';
    }

    if (!response.ok && (!data.message || data.message.trim().length === 0)) {
      data.message = `Request failed (${response.status})`;
      data.success = false;
      data.data = null;
    }

    return data;
  }

  // Auth
  async login(emailOrMobile: string, password: string) {
    return this.request<LoginResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ emailOrMobile, password }),
    });
  }

  async loginWithGoogle(idToken: string) {
    return this.request<LoginResponse>('/auth/google', {
      method: 'POST',
      body: JSON.stringify({ idToken }),
    });
  }

  async selectTenant(emailOrMobile: string, password: string, tenantId: number) {
    return this.request<LoginResponse>('/auth/select-tenant', {
      method: 'POST',
      body: JSON.stringify({ emailOrMobile, password, tenantId }),
    });
  }

  // Tenants
  async getTenants() {
    return this.request<TenantDto[]>('/tenants');
  }
  async createTenant(data: CreateTenantRequest) {
    return this.request<TenantDto>('/tenants', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
  async createTenantWithCsv(data: {
    name: string;
    email: string;
    billingCycleDay?: number;
    reportEmail?: string;
    address?: string;
    adminPassword?: string;
    csvFile?: File | null;
  }) {
    const formData = new FormData();
    formData.append('name', data.name);
    formData.append('email', data.email);
    if (data.billingCycleDay) formData.append('billingCycleDay', String(data.billingCycleDay));
    if (data.reportEmail) formData.append('reportEmail', data.reportEmail);
    if (data.address) formData.append('address', data.address);
    if (data.adminPassword) formData.append('adminPassword', data.adminPassword);
    if (data.csvFile) formData.append('csvFile', data.csvFile);
    const token = this.getToken();
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(`${API_BASE}/tenants/onboard-from-csv`, {
      method: 'POST',
      headers,
      body: formData,
    });
    return response.json();
  }
  async downloadOnboardingSampleCsv() {
    const token = this.getToken();
    const response = await fetch(`${API_BASE}/tenants/onboard-from-csv/sample`, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    });
    if (!response.ok) throw new Error('Download failed');
    return response.blob();
  }
  async downloadOnboardingSampleExcel() {
    const token = this.getToken();
    const response = await fetch(`${API_BASE}/tenants/onboard-from-excel/sample`, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    });
    if (!response.ok) throw new Error('Download failed');
    return response.blob();
  }
  async createTenantWithExcel(data: {
    name: string;
    email: string;
    billingCycleDay?: number;
    reportEmail?: string;
    address?: string;
    adminPassword?: string;
    excelFile?: File | null;
  }) {
    const formData = new FormData();
    formData.append('name', data.name);
    formData.append('email', data.email);
    if (data.billingCycleDay) formData.append('billingCycleDay', String(data.billingCycleDay));
    if (data.reportEmail) formData.append('reportEmail', data.reportEmail);
    if (data.address) formData.append('address', data.address);
    if (data.adminPassword) formData.append('adminPassword', data.adminPassword);
    if (data.excelFile) formData.append('excelFile', data.excelFile);
    const token = this.getToken();
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(`${API_BASE}/tenants/onboard-from-excel`, {
      method: 'POST',
      headers,
      body: formData,
    });
    return response.json();
  }
  async onboardFromExcel(tenantId: number, excelFile: File) {
    const formData = new FormData();
    formData.append('excelFile', excelFile);
    const token = this.getToken();
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(`${API_BASE}/tenants/${tenantId}/onboard-from-excel`, {
      method: 'POST',
      headers,
      body: formData,
    });
    return response.json();
  }
  async downloadErrorExcel(errors: Array<{ sheetName: string; rowNumber: number; columnName: string; errorCode: string; errorMessage: string; suggestion: string; originalValue?: string }>) {
    const token = this.getToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(`${API_BASE}/tenants/download-error-excel`, {
      method: 'POST',
      headers,
      body: JSON.stringify(errors),
    });
    if (!response.ok) throw new Error('Download failed');
    return response.blob();
  }
  async updateTenant(id: number, data: UpdateTenantRequest) {
    return this.request<TenantDto>(`/tenants/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }
  async clearTenantData(id: number) {
    return this.request<ClearTenantDataResult>(`/tenants/${id}/clear-data`, {
      method: 'DELETE',
    });
  }
  async deleteTenant(id: number) {
    return this.request<unknown>(`/tenants/${id}`, {
      method: 'DELETE',
    });
  }
  async downloadTenantBackup(id: number) {
    const token = this.getToken();
    const response = await fetch(`${API_BASE}/tenants/${id}/backup`, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    });
    if (!response.ok) throw new Error('Backup download failed');
    return response.blob();
  }
  async restoreTenantBackup(id: number, data: TenantBackupDto) {
    return this.request<TenantRestoreResult>(`/tenants/${id}/restore`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
  async resetTenantAdminPassword(id: number, newPassword: string) {
    return this.request<unknown>(`/tenants/${id}/admin-password`, {
      method: 'PUT',
      body: JSON.stringify({ newPassword }),
    });
  }
  async copyTenant(sourceTenantId: number, data: CopyTenantRequest) {
    return this.request<TenantDto>(`/tenants/${sourceTenantId}/copy`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
  async upgradeTenantSubscription(id: number, plan: 'Monthly' | 'Yearly') {
    return this.request<TenantDto>(`/tenants/${id}/subscription`, {
      method: 'PUT',
      body: JSON.stringify({ plan }),
    });
  }
  async renewTenantSubscription(id: number) {
    return this.request<TenantDto>(`/tenants/${id}/subscription/renew`, { method: 'POST' });
  }
  async suspendTenant(id: number, reason?: string) {
    return this.request<TenantDto>(`/tenants/${id}/suspend`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }
  async reactivateTenant(id: number) {
    return this.request<TenantDto>(`/tenants/${id}/reactivate`, { method: 'POST' });
  }
  async disableAllTenantUsers(id: number) {
    return this.request<unknown>(`/tenants/${id}/disable-all-users`, { method: 'POST' });
  }

  // Users
  async getUsers() {
    return this.request<UserDto[]>('/users');
  }
  async createUser(data: CreateUserRequest) {
    return this.request<UserDto>('/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
  async updateProfile(data: UpdateProfileRequest) {
    return this.request<UserDto>('/users/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }
  async getProfile() {
    return this.request<UserDto>('/users/profile');
  }
  async changeOwnPassword(currentPassword: string, newPassword: string) {
    return this.request<unknown>('/users/profile/change-password', {
      method: 'PUT',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  }
  async updateUser(id: number, data: UpdateProfileRequest) {
    return this.request<UserDto>(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }
  async resetUserPassword(id: number, newPassword: string) {
    return this.request<unknown>(`/users/${id}/reset-password`, {
      method: 'PUT',
      body: JSON.stringify({ newPassword }),
    });
  }
  async activateUser(id: number) {
    return this.request<unknown>(`/users/${id}/activate`, { method: 'PUT' });
  }
  async deactivateUser(id: number) {
    return this.request<unknown>(`/users/${id}/deactivate`, { method: 'PUT' });
  }
  async deleteUser(id: number) {
    return this.request<unknown>(`/users/${id}`, { method: 'DELETE' });
  }
  async unlockUser(id: number) {
    return this.request<unknown>(`/users/${id}/unlock`, { method: 'PUT' });
  }
  async forcePasswordResetUser(id: number) {
    return this.request<unknown>(`/users/${id}/force-password-reset`, { method: 'PUT' });
  }

  // Delivery Boys
  async getDeliveryBoys() {
    return this.request<DeliveryBoyDto[]>('/deliveryboys');
  }
  async createDeliveryBoy(data: CreateDeliveryBoyRequest) {
    return this.request<unknown>('/deliveryboys', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
  async updateDeliveryBoy(id: number, data: UpdateDeliveryBoyRequest) {
    return this.request<unknown>(`/deliveryboys/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }
  async updateDeliveryBoySocieties(id: number, societies: string[]) {
    return this.request<unknown>(`/deliveryboys/${id}/societies`, {
      method: 'PUT',
      body: JSON.stringify({ assignedSocieties: societies }),
    });
  }
  async deleteDeliveryBoy(id: number) {
    return this.request<unknown>(`/deliveryboys/${id}`, { method: 'DELETE' });
  }
  async getAvailableSocieties() {
    return this.request<string[]>('/deliveryboys/available-societies');
  }
  async downloadDeliveryBoySampleCsv() {
    const token = this.getToken();
    const response = await fetch(`${API_BASE}/deliveryboys/bulk-upload/sample`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response.blob();
  }
  async bulkUploadDeliveryBoys(file: File) {
    const token = this.getToken();
    const formData = new FormData();
    formData.append('file', file);
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    const response = await fetch(`${API_BASE}/deliveryboys/bulk-upload`, {
      method: 'POST',
      headers,
      body: formData,
    });
    return response.json();
  }

  // Sessions (SuperAdmin)
  async getAllSessions() {
    return this.request<UserSessionDto[]>('/sessions');
  }
  async getTenantSessions() {
    return this.request<UserSessionDto[]>('/sessions/tenant');
  }
  async forceLogoff(sessionId: number) {
    return this.request<unknown>(`/sessions/${sessionId}`, { method: 'DELETE' });
  }

  // Milk Types
  async getMilkTypes() {
    return this.request<MilkTypeDto[]>('/milktypes');
  }
  async createMilkType(data: CreateMilkTypeRequest) {
    return this.request<MilkTypeDto>('/milktypes', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
  async updateMilkType(id: number, data: UpdateMilkTypeRequest) {
    return this.request<MilkTypeDto>(`/milktypes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }
  async bulkUploadMilkTypes(file: File, tenantId?: number) {
    const formData = new FormData();
    formData.append('file', file);
    const token = this.getToken();
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const url = tenantId ? `${API_BASE}/milktypes/bulk-upload?tenantId=${tenantId}` : `${API_BASE}/milktypes/bulk-upload`;
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });
    return response.json();
  }
  async downloadMilkTypeSampleCsv() {
    const token = this.getToken();
    const response = await fetch(`${API_BASE}/milktypes/bulk-upload/sample`, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    });
    if (!response.ok) throw new Error('Download failed');
    return response.blob();
  }

  // Subscribers
  async getSubscribers() {
    return this.request<SubscriberDto[]>('/subscribers');
  }
  async createSubscriber(data: CreateSubscriberRequest) {
    return this.request<SubscriberDto>('/subscribers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
  async pauseSubscriber(id: number, data: PauseRequest) {
    return this.request<PauseDto>(`/subscribers/${id}/pause`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
  async resumeSubscriber(id: number) {
    return this.request<unknown>(`/subscribers/${id}/resume`, {
      method: 'POST',
    });
  }
  async activateSubscriber(id: number) {
    return this.request<unknown>(`/subscribers/${id}/activate`, {
      method: 'POST',
    });
  }
  async suspendSubscriber(id: number, reason?: string) {
    return this.request<SubscriberDto>(`/subscribers/${id}/suspend`, {
      method: 'POST',
      body: JSON.stringify({ reason: reason || 'Suspended from billing' }),
    });
  }
  async reactivateSubscriber(id: number) {
    return this.request<SubscriberDto>(`/subscribers/${id}/reactivate`, {
      method: 'POST',
    });
  }

  async getSubscriberPauses(id: number) {
    return this.request<PauseDto[]>(`/subscribers/${id}/pauses`);
  }

  async deleteSubscriberPause(subscriberId: number, pauseId: number) {
    return this.request<unknown>(`/subscribers/${subscriberId}/pauses/${pauseId}`, {
      method: 'DELETE',
    });
  }

  async resetSubscriberPassword(id: number, newPassword: string) {
    return this.request<unknown>(`/subscribers/${id}/reset-password`, {
      method: 'PUT',
      body: JSON.stringify({ newPassword }),
    });
  }

  async bulkUploadSubscribers(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    const token = this.getToken();
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const response = await fetch(`${API_BASE}/subscribers/bulk-upload`, {
      method: 'POST',
      headers,
      body: formData,
    });
    return response.json();
  }

  async downloadSampleCsv() {
    const token = this.getToken();
    const response = await fetch(`${API_BASE}/subscribers/bulk-upload/sample`, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    });
    if (!response.ok) throw new Error('Download failed');
    return response.blob();
  }

  async getAddressMasters() {
    return this.request<AddressMasterDto[]>('/address-masters');
  }

  async createAddressMaster(data: { societyName: string; area: string; city: string }) {
    return this.request<AddressMasterDto>('/address-masters', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAddressMaster(id: number, data: { societyName?: string; area?: string; city?: string; isActive?: boolean }) {
    return this.request<AddressMasterDto>(`/address-masters/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async bulkUploadAddressMasters(file: File, tenantId?: number) {
    const formData = new FormData();
    formData.append('file', file);
    const token = this.getToken();
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const q = tenantId ? `?tenantId=${tenantId}` : '';

    const response = await fetch(`${API_BASE}/address-masters/bulk-upload${q}`, {
      method: 'POST',
      headers,
      body: formData,
    });
    return response.json();
  }

  async downloadAddressMasterSampleCsv() {
    const token = this.getToken();
    const response = await fetch(`${API_BASE}/address-masters/bulk-upload/sample`, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    });
    if (!response.ok) throw new Error('Download failed');
    return response.blob();
  }

  // Building Masters
  async getBuildingMasters(societyId?: number) {
    const q = societyId ? `?societyId=${societyId}` : '';
    return this.request<BuildingMasterDto[]>(`/building-masters${q}`);
  }

  async createBuildingMaster(data: { addressMasterId: number; name: string }) {
    return this.request<BuildingMasterDto>('/building-masters', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateBuildingMaster(id: number, data: { name?: string; isActive?: boolean }) {
    return this.request<BuildingMasterDto>(`/building-masters/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async bulkUploadBuildingMasters(file: File, tenantId?: number) {
    const formData = new FormData();
    formData.append('file', file);
    const token = this.getToken();
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const q = tenantId ? `?tenantId=${tenantId}` : '';

    const response = await fetch(`${API_BASE}/building-masters/bulk-upload${q}`, {
      method: 'POST',
      headers,
      body: formData,
    });
    return response.json();
  }

  async downloadBuildingMasterSampleCsv() {
    const token = this.getToken();
    const response = await fetch(`${API_BASE}/building-masters/bulk-upload/sample`, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    });
    if (!response.ok) throw new Error('Download failed');
    return response.blob();
  }

  // Flat Masters
  async getFlatMasters(buildingId?: number) {
    const q = buildingId ? `?buildingId=${buildingId}` : '';
    return this.request<FlatMasterDto[]>(`/flat-masters${q}`);
  }

  async createFlatMaster(data: { buildingMasterId: number; name: string }) {
    return this.request<FlatMasterDto>('/flat-masters', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateFlatMaster(id: number, data: { name?: string; isActive?: boolean }) {
    return this.request<FlatMasterDto>(`/flat-masters/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async bulkUploadFlatMasters(file: File, tenantId?: number) {
    const formData = new FormData();
    formData.append('file', file);
    const token = this.getToken();
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const q = tenantId ? `?tenantId=${tenantId}` : '';

    const response = await fetch(`${API_BASE}/flat-masters/bulk-upload${q}`, {
      method: 'POST',
      headers,
      body: formData,
    });
    return response.json();
  }

  async downloadFlatMasterSampleCsv() {
    const token = this.getToken();
    const response = await fetch(`${API_BASE}/flat-masters/bulk-upload/sample`, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    });
    if (!response.ok) throw new Error('Download failed');
    return response.blob();
  }

  // Deliveries
  async getDeliveries(date?: string) {
    const q = date ? `?date=${date}` : '';
    return this.request<DeliveryDto[]>(`/deliveries${q}`);
  }
  async getMonthlyDeliveries(month?: string) {
    const q = month ? `?month=${month}` : '';
    return this.request<DeliveryDto[]>(`/deliveries/monthly${q}`);
  }
  async generateDeliveries(date?: string) {
    const q = date ? `?date=${date}` : '';
    return this.request<{ deliveredCount: number; date: string }>(`/deliveries/generate${q}`, {
      method: 'POST',
    });
  }
  async exportDeliveriesCsv(date?: string) {
    const q = date ? `?date=${date}` : '';
    const token = this.getToken();
    const response = await fetch(`${API_BASE}/deliveries/export${q}`, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    });
    if (!response.ok) throw new Error('Export failed');
    return response.blob();
  }
  async updateDelivery(id: number, data: { quantity?: number; status?: string }) {
    return this.request<DeliveryDto>(`/deliveries/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }
  async correctDelivery(id: number, data: UpdateDeliveryWithReasonRequest) {
    return this.request<DeliveryDto>(`/deliveries/${id}/correct`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }
  async getDeliveryCorrectionLogs(id: number) {
    return this.request<DeliveryCorrectionLogDto[]>(`/deliveries/${id}/correction-logs`);
  }
  async batchUpdateDeliveryStatus(deliveryIds: number[], status: string) {
    return this.request<{ updatedCount: number }>('/deliveries/batch-status', {
      method: 'PUT',
      body: JSON.stringify({ deliveryIds, status }),
    });
  }
  async addExtraDelivery(data: { subscriberId: number; date: string; quantity: number; milkTypeId?: number }) {
    return this.request<DeliveryDto>('/deliveries/extra', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
  async getDeliveryLogs(params: { fromDate?: string; toDate?: string; deliveryBoyUserId?: number; societyName?: string; buildingName?: string; status?: string }) {
    const query = new URLSearchParams();
    if (params.fromDate) query.append('fromDate', params.fromDate);
    if (params.toDate) query.append('toDate', params.toDate);
    if (params.deliveryBoyUserId) query.append('deliveryBoyUserId', String(params.deliveryBoyUserId));
    if (params.societyName) query.append('societyName', params.societyName);
    if (params.buildingName) query.append('buildingName', params.buildingName);
    if (params.status) query.append('status', params.status);
    const qs = query.toString();
    return this.request<{ deliveries: DeliveryLogDto[]; summary: DeliveryLogSummaryDto }>(`/deliveries/logs${qs ? '?' + qs : ''}`);
  }

  // Billing
  async getBills() {
    return this.request<BillDto[]>('/billing');
  }
  async getBillsBySubscriber(subscriberId: number) {
    return this.request<BillDto[]>(`/billing/${subscriberId}`);
  }
  async generateBills(month: string) {
    return this.request<BillDto[]>('/billing/generate', {
      method: 'POST',
      body: JSON.stringify({ month }),
    });
  }
  async generateBillForSubscriber(subscriberId: number) {
    return this.request<BillDto>('/billing/generate-subscriber', {
      method: 'POST',
      body: JSON.stringify({ subscriberId }),
    });
  }
  async markBillPaid(billId: number) {
    return this.request<BillDto>(`/billing/${billId}/mark-paid`, {
      method: 'PUT',
    });
  }
  async revokeBill(billId: number, data: RevokeBillRequest) {
    return this.request<BillDto>(`/billing/${billId}/revoke`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
  async reGenerateBill(billId: number) {
    return this.request<BillDto>(`/billing/${billId}/re-generate`, {
      method: 'POST',
    });
  }
  async raiseBillIssue(billId: number, data: RaiseBillIssueRequest) {
    return this.request<BillIssueDto>(`/billing/${billId}/raise-issue`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
  async getBillIssues() {
    return this.request<BillIssueDto[]>('/billing/bill-issues');
  }
  async resolveBillIssue(issueId: number, data: ResolveBillIssueRequest) {
    return this.request<BillIssueDto>(`/billing/bill-issues/${issueId}/resolve`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }
  async undoBillPaid(billId: number) {
    return this.request<BillDto>(`/billing/${billId}/undo-paid`, {
      method: 'PUT',
    });
  }
  async getBillDetail(billId: number) {
    return this.request<BillDetailDto>(`/billing/${billId}/detail`);
  }
  async getPublicBillDetail(billId: number, sig?: string, exp?: number) {
    const params = new URLSearchParams();
    if (sig) params.set('sig', sig);
    if (exp) params.set('exp', String(exp));
    const qs = params.toString();
    const url = `${API_BASE}/billing/public/${billId}/detail${qs ? `?${qs}` : ''}`;
    const response = await fetch(url);
    const raw = await this.readResponseBody(response);
    return this.parseApiResponse<BillDetailDto>(raw, 'Could not load bill');
  }
  async downloadBillPdf(billId: number) {
    await this.downloadFile(`/billing/${billId}/pdf`, `bill-${billId}.pdf`, 'application/pdf');
  }
  async sendPaymentReminder(data: SendReminderRequest) {
    return this.request<{ count: number }>('/billing/send-reminder', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
  async getConsolidatedBills(monthsBack = 2) {
    return this.request<ConsolidatedBillDto[]>(`/billing/consolidated?monthsBack=${monthsBack}`);
  }
  async getConsolidatedBillsBySubscriber(subscriberId: number) {
    return this.request<ConsolidatedBillDto>(`/billing/consolidated/subscriber/${subscriberId}`);
  }
  async markAllBillsPaid(data: MarkAllBillsPaidRequest) {
    return this.request<unknown>('/billing/consolidated/mark-paid', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
  async sendConsolidatedReminder(data: SendConsolidatedReminderRequest) {
    return this.request<{ count: number }>('/billing/consolidated/send-reminder', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
  async sendBillWhatsApp(billId: number) {
    return this.request<{ sent: boolean }>('/billing/send-whatsapp', {
      method: 'POST',
      body: JSON.stringify({ billId }),
    });
  }
  async sendConsolidatedWhatsApp(data: SendConsolidatedWhatsAppRequest) {
    return this.request<{ sent: boolean }>('/billing/consolidated/send-whatsapp', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
  async getReminderLogs(filters?: { subscriberName?: string; from?: string; to?: string }) {
    const params = new URLSearchParams();
    if (filters?.subscriberName) params.set('subscriberName', filters.subscriberName);
    if (filters?.from) params.set('from', filters.from);
    if (filters?.to) params.set('to', filters.to);
    const qs = params.toString();
    return this.request<ReminderLogDto[]>(`/billing/reminder-logs${qs ? `?${qs}` : ''}`);
  }
  async updateSubscriber(id: number, data: UpdateSubscriberRequest) {
    return this.request<SubscriberDto>(`/subscribers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }
  async deleteSubscriber(id: number) {
    return this.request<unknown>(`/subscribers/${id}`, {
      method: 'DELETE',
    });
  }
  async getSubscriptionChanges(id: number) {
    return this.request<SubscriptionChangeDto[]>(`/subscribers/${id}/subscription-changes`);
  }

  // Payments
  async createPayment(data: CreatePaymentRequest) {
    return this.request<PaymentDto>('/payments', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Reports
  async getDailyReport(date?: string) {
    const q = date ? `?date=${date}` : '';
    return this.request<DailyReportDto>(`/reports/daily${q}`);
  }

  async getMonthlyDeliveryReport(month?: string) {
    const q = month ? `?month=${month}` : '';
    return this.request<MonthlyDeliveryReportDto>(`/reports/monthly-delivery${q}`);
  }

  async getMonthlyPaymentReport(month?: string) {
    const q = month ? `?month=${month}` : '';
    return this.request<MonthlyPaymentReportDto>(`/reports/monthly-payment${q}`);
  }

  async getSubscriberWiseReport(month?: string) {
    const q = month ? `?month=${month}` : '';
    return this.request<SubscriberWiseReportDto>(`/reports/subscriber-wise${q}`);
  }

  async getYearlyReport(year?: number) {
    const q = year ? `?year=${year}` : '';
    return this.request<YearlyReportDto>(`/reports/yearly${q}`);
  }

  async getGstReport(month?: string) {
    const q = month ? `?month=${month}` : '';
    return this.request<GstReportDto>(`/reports/gst${q}`);
  }

  async getTenantPerformanceReport(month?: string) {
    const q = month ? `?month=${month}` : '';
    return this.request<TenantPerformanceReportDto>(`/reports/superadmin/tenant-performance${q}`);
  }

  // Users Report
  async exportUsersCsv() {
    const token = this.getToken();
    const response = await fetch(`${API_BASE}/users/export`, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    });
    if (!response.ok) throw new Error('Export failed');
    return response.blob();
  }

  // Email Config
  async getEmailConfig() {
    return this.request<EmailConfigDto | null>('/email-config');
  }
  async saveEmailConfig(data: EmailConfigRequest) {
    return this.request<EmailConfigDto>('/email-config', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
  async testEmail(toEmail: string) {
    return this.request<boolean>('/email-config/test', {
      method: 'POST',
      body: JSON.stringify({ toEmail }),
    });
  }

  // WhatsApp Config
  async getWhatsAppConfig() {
    return this.request<WhatsAppConfigDto | null>('/whatsapp-config');
  }
  async saveWhatsAppConfig(data: WhatsAppConfigRequest) {
    return this.request<WhatsAppConfigDto>('/whatsapp-config', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
  async testWhatsApp(toPhone: string) {
    return this.request<boolean>('/whatsapp-config/test', {
      method: 'POST',
      body: JSON.stringify({ toPhone }),
    });
  }

  // Notifications
  async sendNotificationToSubscribers(data: SendNotificationRequest) {
    return this.request<NotificationDto>('/notifications/send-subscribers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
  async sendNotificationToTenants(data: SendNotificationRequest) {
    return this.request<NotificationDto>('/notifications/send-tenants', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
  async sendNotificationToAdmin(data: SendNotificationRequest) {
    return this.request<NotificationDto>('/notifications/send-admin', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
  async getNotifications() {
    return this.request<NotificationDto[]>('/notifications');
  }
  async getUnreadNotificationCount() {
    return this.request<number>('/notifications/unread-count');
  }
  async markNotificationRead(id: number) {
    return this.request<unknown>(`/notifications/${id}/read`, {
      method: 'PUT',
    });
  }
  async markAllNotificationsRead() {
    return this.request<unknown>('/notifications/read-all', {
      method: 'PUT',
    });
  }

  // Branding
  async getBranding() {
    return this.request<TenantBrandingDto | null>('/branding');
  }
  async getBrandingByTenantId(tenantId: number) {
    return this.request<TenantBrandingDto | null>(`/branding/${tenantId}`);
  }
  async updateBranding(data: UpdateBrandingRequest) {
    return this.request<TenantBrandingDto>('/branding', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }
  async updateMenuOrderForAll(menuOrderJson: string) {
    return this.request<null>('/branding/menu-order-all', {
      method: 'PUT',
      body: JSON.stringify({ menuOrderJson }),
    });
  }

  // Cash Management
  async saveDailyCash(data: SaveDailyCashRequest) {
    return this.request<DailyCashEntryDto>('/cash/daily', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
  async getDailyCash(date: string) {
    return this.request<DailyCashEntryDto | null>(`/cash/daily/${date}`);
  }
  async getDailyCashRange(from: string, to: string) {
    return this.request<DailyCashEntryDto[]>(`/cash/daily?from=${from}&to=${to}`);
  }
  async addExpense(data: AddExpenseRequest) {
    return this.request<DailyExpenseDto>('/cash/expense', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
  async deleteExpense(id: number) {
    return this.request<boolean>(`/cash/expense/${id}`, {
      method: 'DELETE',
    });
  }
  async getMonthlyCashSummary(month: string) {
    return this.request<MonthlyCashSummaryDto>(`/cash/monthly/${month}`);
  }
  async exportMonthlyCashExcel(month: string) {
    const token = this.getToken();
    const response = await fetch(`${API_BASE}/cash/monthly/${month}/export`, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    });
    if (!response.ok) throw new Error('Export failed');
    return response.blob();
  }
  async getAllTenantsCashReport(month: string) {
    return this.request<TenantCashReportDto[]>(`/cash/reports/all-tenants/${month}`);
  }
  async getYearlyCashSummary(year: string) {
    return this.request<YearlyCashSummaryDto>(`/cash/yearly/${year}`);
  }
  async exportYearlyCashExcel(year: string) {
    const token = this.getToken();
    const response = await fetch(`${API_BASE}/cash/yearly/${year}/export`, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    });
    if (!response.ok) throw new Error('Export failed');
    return response.blob();
  }
  async getAllTenantsYearlyCashReport(year: string) {
    return this.request<TenantYearlyCashReportDto[]>(`/cash/reports/all-tenants/yearly/${year}`);
  }

  // === Expense Categories ===
  async getExpenseCategories() {
    return this.request<ExpenseCategoryDto[]>('/cash/categories');
  }
  async createExpenseCategory(name: string) {
    return this.request<ExpenseCategoryDto>('/cash/categories', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  }
  async deleteExpenseCategory(id: number) {
    return this.request<boolean>(`/cash/categories/${id}`, {
      method: 'DELETE',
    });
  }

  // Dashboard Metrics
  async getDashboardMetrics() {
    return this.request<DashboardMetricsDto>('/reports/dashboard-metrics');
  }
  async getCashFlowDashboard() {
    return this.request<CashFlowDashboardDto>('/reports/cash-flow-dashboard');
  }

  // ===== AI Documents =====
  async uploadDocument(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    const token = localStorage.getItem('milk_token');
    const res = await fetch(`${API_BASE}/documents/upload`, {
      method: 'POST',
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: formData,
    });

    const raw = await this.readResponseBody(res);
    const data = this.parseApiResponse<{ name: string }>(raw, `Upload failed (${res.status})`);

    if (res.status === 401) { localStorage.removeItem('milk_token'); localStorage.removeItem('milk_user'); window.location.href = '/'; }
    return data;
  }
  async getDocuments() {
    return this.request<DocumentDto[]>('/documents');
  }
  async downloadDocument(name: string) {
    const token = localStorage.getItem('milk_token');
    const res = await fetch(`${API_BASE}/documents/${encodeURIComponent(name)}/download`, {
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    if (!res.ok) throw new Error('Download failed');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = name; a.click();
    URL.revokeObjectURL(url);
  }
  async deleteDocument(name: string) {
    return this.request<unknown>(`/documents/${encodeURIComponent(name)}`, { method: 'DELETE' });
  }

  // ===== Record Store =====
  async getRecordFiles(tenantId?: number) {
    const q = tenantId != null ? `?tenantId=${tenantId}` : '';
    return this.request<RecordFileDto[]>(`/recordstore/files${q}`);
  }
  async uploadRecordFile(file: File, tenantId?: number) {
    const formData = new FormData();
    formData.append('file', file);
    const token = localStorage.getItem('milk_token');
    const q = tenantId != null ? `?tenantId=${tenantId}` : '';
    const res = await fetch(`${API_BASE}/recordstore/upload${q}`, {
      method: 'POST',
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: formData,
    });
    const raw = await this.readResponseBody(res);
    if (res.status === 401) { localStorage.removeItem('milk_token'); localStorage.removeItem('milk_user'); window.location.href = '/'; }
    return this.parseApiResponse<RecordFileDto>(raw, `Upload failed (${res.status})`);
  }
  async downloadRecordFile(fileName: string, tenantId?: number) {
    const token = localStorage.getItem('milk_token');
    const q = tenantId != null ? `?tenantId=${tenantId}` : '';
    const res = await fetch(`${API_BASE}/recordstore/files/${encodeURIComponent(fileName)}/download${q}`, {
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    if (!res.ok) throw new Error('Download failed');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = fileName; a.click();
    URL.revokeObjectURL(url);
  }
  async deleteRecordFile(fileName: string, tenantId?: number) {
    const q = tenantId != null ? `?tenantId=${tenantId}` : '';
    return this.request<unknown>(`/recordstore/files/${encodeURIComponent(fileName)}${q}`, { method: 'DELETE' });
  }
  async getRecordStoreTenants() {
    return this.request<RecordStoreTenantDto[]>('/recordstore/tenants');
  }

  // ===== AI Chat =====
  async askChat(question: string) {
    return this.request<{ answer: string }>('/chat', {
      method: 'POST',
      body: JSON.stringify({ question }),
    });
  }

  // ===== Purchase Entries =====
  async getPurchases(filter?: PurchaseSaleFilter) {
    const params = buildQueryString(filter);
    return this.request<PurchaseEntryDto[]>(`/purchasesale/purchases${params}`);
  }
  async getPurchaseById(id: number) {
    return this.request<PurchaseEntryDto>(`/purchasesale/purchases/${id}`);
  }
  async createPurchase(data: CreatePurchaseEntryRequest) {
    return this.request<PurchaseEntryDto>('/purchasesale/purchases', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
  async updatePurchase(id: number, data: UpdatePurchaseEntryRequest) {
    return this.request<PurchaseEntryDto>(`/purchasesale/purchases/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }
  async deletePurchase(id: number) {
    return this.request<unknown>(`/purchasesale/purchases/${id}`, { method: 'DELETE' });
  }
  async exportPurchasesCsv(filter?: PurchaseSaleFilter) {
    const params = buildQueryString(filter);
    await this.downloadFile(`/purchasesale/purchases/export/csv${params}`, `purchases_${today()}.csv`, 'text/csv');
  }

  // ===== Sale Entries =====
  async getSales(filter?: PurchaseSaleFilter) {
    const params = buildQueryString(filter);
    return this.request<SaleEntryDto[]>(`/purchasesale/sales${params}`);
  }
  async getSaleById(id: number) {
    return this.request<SaleEntryDto>(`/purchasesale/sales/${id}`);
  }
  async createSale(data: CreateSaleEntryRequest) {
    return this.request<SaleEntryDto>('/purchasesale/sales', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
  async updateSale(id: number, data: UpdateSaleEntryRequest) {
    return this.request<SaleEntryDto>(`/purchasesale/sales/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }
  async deleteSale(id: number) {
    return this.request<unknown>(`/purchasesale/sales/${id}`, { method: 'DELETE' });
  }
  async exportSalesCsv(filter?: PurchaseSaleFilter) {
    const params = buildQueryString(filter);
    await this.downloadFile(`/purchasesale/sales/export/csv${params}`, `sales_${today()}.csv`, 'text/csv');
  }

  // ===== Purchase/Sale Reports =====
  async getPurchaseDailyReport(date: string) {
    return this.request<PurchaseDailyReport>(`/purchasesale/reports/purchase/daily?date=${date}`);
  }
  async getPurchaseMonthlyReport(month: string) {
    return this.request<PurchaseMonthlyReport>(`/purchasesale/reports/purchase/monthly?month=${month}`);
  }
  async getPurchaseYearlyReport(year: number) {
    return this.request<PurchaseYearlyReport>(`/purchasesale/reports/purchase/yearly?year=${year}`);
  }
  async getSaleDailyReport(date: string) {
    return this.request<SaleDailyReport>(`/purchasesale/reports/sale/daily?date=${date}`);
  }
  async getSaleMonthlyReport(month: string) {
    return this.request<SaleMonthlyReport>(`/purchasesale/reports/sale/monthly?month=${month}`);
  }
  async getSaleYearlyReport(year: number) {
    return this.request<SaleYearlyReport>(`/purchasesale/reports/sale/yearly?year=${year}`);
  }
  async getPurchaseVsSaleSummary(month: string) {
    return this.request<PurchaseVsSaleSummary>(`/purchasesale/reports/summary?month=${month}`);
  }
  async getPurchaseSaleDashboard() {
    return this.request<PurchaseSaleDashboard>('/purchasesale/dashboard');
  }

  // ===== Module Access =====
  async getAllModules() {
    return this.request<AppModuleDto[]>('/modules');
  }
  async getEnabledModules() {
    return this.request<TenantEnabledModules>('/modules/enabled');
  }
  async getEnabledModulesForTenant(tenantId: number) {
    return this.request<TenantEnabledModules>(`/modules/enabled/${tenantId}`);
  }
  async getModuleMatrix() {
    return this.request<TenantModuleMatrix[]>('/modules/matrix');
  }
  async getModuleMatrixForTenant(tenantId: number) {
    return this.request<TenantModuleMatrix>(`/modules/matrix/${tenantId}`);
  }
  async setModuleAccess(tenantId: number, moduleKey: string, isEnabled: boolean) {
    return this.request<unknown>('/modules/access', {
      method: 'PUT',
      body: JSON.stringify({ tenantId, moduleKey, isEnabled }),
    });
  }
  async bulkSetModuleAccess(tenantId: number, moduleAccess: Record<string, boolean>) {
    return this.request<unknown>('/modules/access/bulk', {
      method: 'PUT',
      body: JSON.stringify({ tenantId, moduleAccess }),
    });
  }

  // ===== Product Types =====
  async getProductTypes(filter?: { search?: string; isActive?: boolean }) {
    const params = buildQueryString(filter as Record<string, unknown>);
    return this.request<ProductTypeDto[]>(`/producttypes${params}`);
  }
  async getProductTypeById(id: number) {
    return this.request<ProductTypeDto>(`/producttypes/${id}`);
  }
  async createProductType(data: CreateProductTypeRequest) {
    return this.request<ProductTypeDto>('/producttypes', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
  async updateProductType(id: number, data: UpdateProductTypeRequest) {
    return this.request<ProductTypeDto>(`/producttypes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }
  async deleteProductType(id: number) {
    return this.request<unknown>(`/producttypes/${id}`, { method: 'DELETE' });
  }
  async bulkCreateProductTypes(items: CreateProductTypeRequest[]) {
    return this.request<BulkCreateProductTypesResult>('/producttypes/bulk', {
      method: 'POST',
      body: JSON.stringify({ items }),
    });
  }

  // Rate history
  async getProductTypeRates(productTypeId: number) {
    return this.request<ProductTypeRateDto[]>(`/producttypes/${productTypeId}/rates`);
  }
  async addProductTypeRate(productTypeId: number, data: CreateProductTypeRateRequest) {
    return this.request<ProductTypeRateDto>(`/producttypes/${productTypeId}/rates`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
  async updateProductTypeRate(productTypeId: number, rateId: number, data: CreateProductTypeRateRequest) {
    return this.request<ProductTypeRateDto>(`/producttypes/${productTypeId}/rates/${rateId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }
  async deleteProductTypeRate(productTypeId: number, rateId: number) {
    return this.request<unknown>(`/producttypes/${productTypeId}/rates/${rateId}`, { method: 'DELETE' });
  }
  async getEffectiveRate(productTypeId: number, date: string) {
    return this.request<EffectiveRateDto | null>(`/producttypes/${productTypeId}/rates/effective?date=${date}`);
  }

  // Audit Log
  async getAuditLogs(filters: AuditLogFilters = {}) {
    const params = new URLSearchParams();
    if (filters.entityType) params.append('entityType', filters.entityType);
    if (filters.actionType) params.append('actionType', filters.actionType);
    if (filters.action) params.append('action', filters.action);
    if (filters.userId) params.append('userId', String(filters.userId));
    if (filters.fromDate) params.append('fromDate', filters.fromDate);
    if (filters.toDate) params.append('toDate', filters.toDate);
    if (filters.search) params.append('search', filters.search);
    if (filters.page) params.append('page', String(filters.page));
    if (filters.pageSize) params.append('pageSize', String(filters.pageSize));
    const qs = params.toString();
    return this.request<AuditLogListResponse>(`/audit${qs ? '?' + qs : ''}`);
  }
  async getAuditLogDetail(id: number) {
    return this.request<AuditLogDto>(`/audit/${id}`);
  }
  async revertAuditAction(id: number, reason?: string) {
    return this.request<unknown>(`/audit/${id}/revert`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }
  async getAuditStats() {
    return this.request<AuditLogStatsDto>('/audit/stats');
  }

  // ===== Tenant Requests =====
  async createTenantRequest(request: CreateTenantRequestRequest) {
    return this.request<TenantRequestDto>('/tenant-requests', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async getTenantRequests() {
    return this.request<TenantRequestDto[]>('/tenant-requests');
  }

  async getTenantRequestById(id: number) {
    return this.request<TenantRequestDto>(`/tenant-requests/${id}`);
  }

  async approveTenantRequest(id: number, request: ApproveTenantRequestRequest) {
    return this.request<TenantRequestDto>(`/tenant-requests/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async rejectTenantRequest(id: number, request: RejectTenantRequestRequest) {
    return this.request<TenantRequestDto>(`/tenant-requests/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }
}

function buildQueryString(obj?: Record<string, unknown>): string {
  if (!obj) return '';
  const p = Object.entries(obj)
    .filter(([, v]) => v != null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  return p.length ? `?${p.join('&')}` : '';
}

function today(): string {
  return new Date().toISOString().slice(0, 10).replace(/-/g, '');
}

// Types
export interface TenantOption {
  tenantId: number;
  tenantName: string;
}

export interface LoginResponse {
  token: string;
  role: string;
  tenantId: number;
  userId: number;
  name: string;
  tenantName: string;
  mustChangePassword?: boolean;
  subscriptionWarningMessage?: string | null;
  requiresTenantSelection?: boolean;
  tenantOptions?: TenantOption[];
}

export interface TenantDto {
  tenantId: number;
  name: string;
  email: string;
  reportEmail: string | null;
  billingCycleDay: number;
  address: string | null;
  isActive: boolean;
  createdDate: string;
  subscriptionPlan: string;
  subscriptionExpiresAt: string | null;
  isSuspended: boolean;
  suspendedReason: string | null;
  lastRenewedDate: string | null;
  daysUntilExpiry: number | null;
  autoCompleteDeliveryHour: number | null;
  autoCompleteDeliveryMinute: number | null;
}

export interface CreateTenantRequest {
  name: string;
  email: string;
  billingCycleDay: number;
  reportEmail?: string;
  adminPassword?: string;
  address?: string;
  logoBase64?: string;
  bannerBase64?: string;
  themeColor?: string;
}

export interface UpdateTenantRequest {
  billingCycleDay?: number;
  reportEmail?: string;
  address?: string;
  isActive?: boolean;
  autoCompleteDeliveryHour?: number;
  autoCompleteDeliveryMinute?: number;
}

export interface CopyTenantRequest {
  newName: string;
  newEmail: string;
  adminPassword: string;
  copyBranding?: boolean;
  copyMilkTypes?: boolean;
  copyAddressMasters?: boolean;
}

export interface UserDto {
  userId: number;
  tenantId: number;
  tenantName: string | null;
  name: string;
  email: string;
  phone: string | null;
  address: string | null;
  role: string;
  isActive: boolean;
  isLocked: boolean;
  mustChangePassword: boolean;
}

export interface UserSessionDto {
  sessionId: number;
  userId: number;
  tenantId: number;
  tenantName: string;
  userName: string;
  role: string;
  loginTime: string;
  logoutTime: string | null;
  isActive: boolean;
  ipAddress: string | null;
}

export interface CreateUserRequest {
  name: string;
  email: string;
  password: string;
  role: string;
  phone?: string;
  address?: string;
}

export interface UpdateProfileRequest {
  name?: string;
  email?: string;
  phone?: string;
  address?: string;
}

export interface ChangeOwnPasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export interface MilkTypeDto {
  milkTypeId: number;
  name: string;
  pricePerLiter: number;
  isActive: boolean;
}

export interface CreateMilkTypeRequest {
  name: string;
  pricePerLiter: number;
}

export interface UpdateMilkTypeRequest {
  name?: string;
  pricePerLiter?: number;
}

export interface SubscriberDto {
  subscriberId: number;
  userId: number;
  tenantId?: number;
  tenantName?: string;
  userName: string;
  userEmail: string;
  userPhone: string | null;
  userAddress: string | null;
  societyName: string | null;
  buildingName: string | null;
  flatName: string | null;
  areaName: string | null;
  city: string | null;
  milkTypeId: number;
  milkTypeName: string;
  dailyQuantity: number;
  pricePerLiter: number;
  startDate: string;
  isActive: boolean;
  isPaused: boolean;
  status?: string;
  suspendedReason?: string | null;
  suspendedAt?: string | null;
}

export interface CreateSubscriberRequest {
  name: string;
  email: string;
  password: string;
  phone?: string;
  address?: string;
  societyName?: string;
  buildingName?: string;
  flatName?: string;
  areaName?: string;
  city?: string;
  milkTypeId: number;
  dailyQuantity: number;
  subscriptionStartDate?: string;
}

export interface PauseRequest {
  startDate: string;
  endDate: string;
}

export interface PauseDto {
  pauseId: number;
  subscriberId: number;
  startDate: string;
  endDate: string;
}

export interface AddressMasterDto {
  addressMasterId: number;
  societyName: string;
  area: string;
  city: string;
  isActive: boolean;
}

export interface BuildingMasterDto {
  buildingMasterId: number;
  addressMasterId: number;
  societyName: string;
  name: string;
  isActive: boolean;
}

export interface FlatMasterDto {
  flatMasterId: number;
  buildingMasterId: number;
  buildingName: string;
  societyName: string;
  name: string;
  isActive: boolean;
}

export interface DeliveryDto {
  deliveryId: number;
  subscriberId: number;
  subscriberName: string;
  subscriberPhone: string | null;
  subscriberAddress: string | null;
  societyName: string | null;
  buildingName: string | null;
  flatName: string | null;
  areaName: string | null;
  city: string | null;
  isNewSubscriber: boolean;
  date: string;
  quantity: number;
  milkTypeName: string;
  pricePerLiter: number;
  status: string;
  isPaused: boolean;
  isExtra: boolean;
}

export interface DeliveryLogDto {
  deliveryId: number;
  subscriberId: number;
  subscriberName: string;
  societyName: string | null;
  buildingName: string | null;
  flatName: string | null;
  date: string;
  quantity: number;
  milkTypeName: string;
  status: string;
  markedByName: string | null;
  markedDate: string | null;
}

export interface DeliveryLogSummaryDto {
  totalDeliveries: number;
  deliveredCount: number;
  pendingCount: number;
  failedCount: number;
  extraDeliveryCount: number;
  byDeliveryBoy: DeliveryBoyDeliverySummaryDto[];
}

export interface DeliveryBoyDeliverySummaryDto {
  userId: number;
  name: string;
  totalMarked: number;
  deliveredCount: number;
  failedCount: number;
}

export interface BillDto {
  billId: number;
  subscriberId: number;
  tenantId?: number;
  tenantName?: string;
  subscriberName: string;
  subscriberPhone?: string | null;
  startDate: string;
  endDate: string;
  totalAmount: number;
  status: string;
  createdDate: string;
  paidAmount: number;
}

export interface RevokeBillRequest {
  reason: string;
}

export interface RaiseBillIssueRequest {
  description: string;
}

export interface ResolveBillIssueRequest {
  resolutionNote?: string;
}

export interface BillIssueDto {
  billIssueId: number;
  tenantId: number;
  billId: number;
  subscriberId: number;
  subscriberName: string;
  description: string;
  status: string;
  submittedDate: string;
  resolvedDate: string | null;
  resolvedByName: string | null;
  resolutionNote: string | null;
}

export interface DeliveryCorrectionLogDto {
  deliveryCorrectionLogId: number;
  deliveryId: number;
  subscriberId: number;
  oldStatus: string;
  newStatus: string;
  oldQuantity: number | null;
  newQuantity: number | null;
  correctedByName: string;
  correctedDate: string;
  correctionReason: string | null;
}

export interface UpdateDeliveryWithReasonRequest {
  quantity?: number;
  status?: string;
  correctionReason?: string;
}

export interface CreatePaymentRequest {
  billId: number;
  amount: number;
  mode: string;
}

export interface PaymentDto {
  paymentId: number;
  billId: number;
  amount: number;
  paymentDate: string;
  mode: string;
}

export interface DailyReportDto {
  date: string;
  totalDeliveries: number;
  skippedDeliveries: number;
  totalQuantity: number;
  deliveries: DeliveryDto[];
}

export interface MonthlyDeliveryReportDto {
  month: string;
  totalDeliveries: number;
  skippedDeliveries: number;
  totalQuantityDelivered: number;
  totalRevenue: number;
  activeSubscribers: number;
  dailyBreakdown: DailyBreakdownDto[];
  milkTypeBreakdown: MilkTypeBreakdownDto[];
}

export interface DailyBreakdownDto {
  date: string;
  delivered: number;
  skipped: number;
  quantity: number;
  revenue: number;
}

export interface MilkTypeBreakdownDto {
  milkType: string;
  quantity: number;
  revenue: number;
  deliveryCount: number;
}

export interface MonthlyPaymentReportDto {
  month: string;
  totalBilled: number;
  totalCollected: number;
  totalOutstanding: number;
  totalBills: number;
  paidBills: number;
  unpaidBills: number;
  collectionRate: number;
  paymentModes: PaymentModeBreakdownDto[];
  recentPayments: PaymentEntryDto[];
}

export interface PaymentModeBreakdownDto {
  mode: string;
  amount: number;
  count: number;
}

export interface PaymentEntryDto {
  paymentId: number;
  subscriberName: string;
  amount: number;
  mode: string;
  paymentDate: string;
  billId: number;
}

export interface SubscriberWiseReportDto {
  month: string;
  subscribers: SubscriberMonthlyDetailDto[];
  grandTotalBilled: number;
  grandTotalPaid: number;
  grandTotalOutstanding: number;
}

export interface YearlyReportDto {
  year: number;
  months: YearlyMonthSummaryDto[];
  totalRevenue: number;
  totalCollected: number;
  totalOutstanding: number;
  totalQuantity: number;
  totalDeliveredCount: number;
  totalSkippedCount: number;
}

export interface YearlyMonthSummaryDto {
  month: string;
  revenue: number;
  collected: number;
  outstanding: number;
  quantity: number;
  deliveredCount: number;
  skippedCount: number;
}

export interface GstReportDto {
  month: string;
  taxableAmount: number;
  gstRatePercent: number;
  gstNo: string | null;
  gstLegalName: string | null;
  gstPlaceOfSupply: string | null;
  gstFilingFrequency: string | null;
  cgstAmount: number;
  sgstAmount: number;
  totalInvoiceAmount: number;
  totalCollected: number;
  outstandingAmount: number;
  totalBills: number;
  paidBills: number;
  unpaidBills: number;
}

export interface TenantPerformanceReportDto {
  month: string;
  tenants: TenantPerformanceEntryDto[];
}

export interface TenantPerformanceEntryDto {
  tenantId: number;
  tenantName: string;
  activeSubscribers: number;
  deliveredCount: number;
  skippedCount: number;
  deliverySuccessRate: number;
  totalBilled: number;
  totalCollected: number;
  outstanding: number;
  collectionRate: number;
  performanceScore: number;
}

export interface SubscriberMonthlyDetailDto {
  subscriberId: number;
  name: string;
  milkType: string;
  dailyQuantity: number;
  deliveredDays: number;
  skippedDays: number;
  pausedDays: number;
  totalQuantity: number;
  totalAmount: number;
  paidAmount: number;
  outstandingAmount: number;
  status: string;
}

export interface ClearTenantDataResult {
  paymentsDeleted: number;
  billsDeleted: number;
  deliveriesDeleted: number;
  pausesDeleted: number;
  subscribersDeleted: number;
  usersDeleted: number;
  milkTypesDeleted: number;
}

export interface TenantBackupDto {
  backupDate: string;
  tenant: TenantDto;
  branding: TenantBrandingDto | null;
  milkTypes: MilkTypeBackupDto[];
  users: UserBackupDto[];
  subscribers: SubscriberBackupDto[];
  pauses: PauseBackupDto[];
  deliveries: DeliveryBackupDto[];
  bills: BillBackupDto[];
  payments: PaymentBackupDto[];
  subscriptionChanges: SubscriptionChangeBackupDto[];
  dailyCashEntries: DailyCashEntryBackupDto[];
  dailyExpenses: DailyExpenseBackupDto[];
  expenseCategories: ExpenseCategoryBackupDto[];
  productTypes: ProductTypeBackupDto[];
  productTypeRates: ProductTypeRateBackupDto[];
}

export interface TenantRestoreResult {
  milkTypesRestored: number;
  usersRestored: number;
  subscribersRestored: number;
  pausesRestored: number;
  deliveriesRestored: number;
  billsRestored: number;
  paymentsRestored: number;
  subscriptionChangesRestored: number;
  dailyCashEntriesRestored: number;
  dailyExpensesRestored: number;
  expenseCategoriesRestored: number;
  productTypesRestored: number;
  productTypeRatesRestored: number;
}

export interface ProductTypeBackupDto {
  productTypeId: number;
  name: string;
  unit: string | null;
  defaultRate: number | null;
  description: string | null;
  displayOrder: number;
  isActive: boolean;
  createdDate: string;
}

export interface ProductTypeRateBackupDto {
  productTypeRateId: number;
  productTypeId: number;
  effectiveDate: string;
  purchaseRate: number;
  saleRate: number;
  notes: string | null;
  createdDate: string;
}

export interface MilkTypeBackupDto {
  milkTypeId: number;
  name: string;
  pricePerLiter: number;
  isActive: boolean;
}

export interface UserBackupDto {
  userId: number;
  name: string;
  email: string;
  phone: string | null;
  address: string | null;
  passwordHash: string;
  role: string;
  isActive: boolean;
  createdDate: string;
}

export interface SubscriberBackupDto {
  subscriberId: number;
  userId: number;
  milkTypeId: number;
  dailyQuantity: number;
  startDate: string;
  isActive: boolean;
}

export interface PauseBackupDto {
  pauseId: number;
  subscriberId: number;
  startDate: string;
  endDate: string;
}

export interface DeliveryBackupDto {
  deliveryId: number;
  subscriberId: number;
  milkTypeId: number;
  date: string;
  quantity: number;
  pricePerLiter: number;
  status: string;
}

export interface BillBackupDto {
  billId: number;
  subscriberId: number;
  startDate: string;
  endDate: string;
  totalAmount: number;
  status: string;
  createdDate: string;
}

export interface PaymentBackupDto {
  paymentId: number;
  billId: number;
  amount: number;
  paymentDate: string;
  mode: string;
}

export interface SubscriptionChangeBackupDto {
  subscriptionChangeId: number;
  subscriberId: number;
  oldMilkTypeId: number;
  newMilkTypeId: number;
  oldQuantity: number;
  newQuantity: number;
  effectiveDate: string;
  createdDate: string;
}

export interface UpdateSubscriberRequest {
  milkTypeId?: number;
  dailyQuantity?: number;
  effectiveDate?: string;
  email?: string;
  phone?: string;
  address?: string;
  societyName?: string;
  buildingName?: string;
  flatName?: string;
  areaName?: string;
  city?: string;
  isActive?: boolean;
}

export interface SubscriptionChangeDto {
  subscriptionChangeId: number;
  subscriberId: number;
  oldMilkTypeName: string;
  newMilkTypeName: string;
  oldQuantity: number;
  newQuantity: number;
  effectiveDate: string;
  createdDate: string;
}

export interface BillDetailDto {
  dairyName: string;
  dairyAddress: string | null;
  dairyEmail: string;
  dairyUpiId: string | null;
  dairyQrCodeBase64: string | null;
  subscriberName: string;
  subscriberPhone: string | null;
  subscriberAddress: string | null;
  billId: number;
  startDate: string;
  endDate: string;
  totalAmount: number;
  paidAmount: number;
  status: string;
  createdDate: string;
  lineItems: BillLineItemDto[];
  subscriptionChanges: SubscriptionChangeDto[];
  payments: PaymentDto[];
}

export interface BillLineItemDto {
  date: string;
  milkTypeName: string;
  quantity: number;
  pricePerLiter: number;
  amount: number;
  isExtra: boolean;
  status: string;
}

export interface SendReminderRequest {
  subscriberId?: number;
  sendEmail: boolean;
  sendNotification: boolean;
}

export interface ReminderLogDto {
  reminderLogId: number;
  subscriberName: string;
  type: string;
  amountDue: number;
  billId: number | null;
  sentByName: string;
  sentDate: string;
}

export interface ConsolidatedBillDto {
  subscriberId: number;
  subscriberName: string;
  subscriberPhone?: string | null;
  billIds: number[];
  unpaidBillCount: number;
  totalUnpaidAmount: number;
  oldestBillDate: string;
  latestBillDate: string;
}

export interface SendConsolidatedReminderRequest {
  subscriberId: number;
  billIds: number[];
}

export interface SendConsolidatedWhatsAppRequest {
  subscriberId: number;
  billIds: number[];
}

export interface MarkAllBillsPaidRequest {
  subscriberId: number;
  paymentMode: string;
}

export interface EmailConfigDto {
  emailConfigId: number;
  provider: string;
  senderEmail: string;
  senderName: string;
  smtpHost: string;
  smtpPort: number;
  useSsl: boolean;
  isActive: boolean;
  updatedDate: string;
}

export interface EmailConfigRequest {
  provider: string;
  senderEmail: string;
  senderPassword: string;
  senderName: string;
  smtpPort?: number;
  useSsl?: boolean;
}

export interface WhatsAppConfigDto {
  whatsAppConfigId: number;
  tenantId: number;
  phoneNumberId: string;
  businessAccountId: string;
  displayPhoneNumber: string;
  businessName: string;
  isActive: boolean;
  createdDate: string;
  updatedDate: string | null;
}

export interface WhatsAppConfigRequest {
  phoneNumberId: string;
  businessAccountId: string;
  accessToken: string;
  displayPhoneNumber: string;
  businessName: string;
  webhookVerifyToken?: string;
}

export interface SendNotificationRequest {
  title: string;
  message: string;
  imageBase64?: string;
  sendEmail?: boolean;
  sendWhatsApp?: boolean;
  targetTenantId?: number;
}

export interface NotificationDto {
  notificationId: number;
  title: string;
  message: string;
  imageUrl: string | null;
  sentByName: string;
  targetRole: string;
  sendEmail: boolean;
  sendWhatsApp: boolean;
  isRead: boolean;
  createdDate: string;
}

export interface TenantBrandingDto {
  tenantBrandingId: number;
  tenantId: number;
  tenantName: string;
  tenantEmail: string;
  address: string | null;
  logoBase64: string | null;
  bannerBase64: string | null;
  qrCodeBase64: string | null;
  themeColor: string;
  mainWording: string | null;
  billHeader: string | null;
  billFooter: string | null;
  gstNo: string | null;
  gstRatePercent: number;
  gstLegalName: string | null;
  gstPlaceOfSupply: string | null;
  gstFilingFrequency: string | null;
  menuOrderJson: string | null;
  upiId: string | null;
  remindViaEmail: boolean;
  remindViaWhatsApp: boolean;
  siteUrl: string | null;
  updatedDate: string;
}

export interface UpdateBrandingRequest {
  logoBase64?: string;
  bannerBase64?: string;
  qrCodeBase64?: string;
  themeColor?: string;
  mainWording?: string;
  billHeader?: string;
  billFooter?: string;
  gstNo?: string;
  gstRatePercent?: number;
  gstLegalName?: string;
  gstPlaceOfSupply?: string;
  gstFilingFrequency?: string;
  menuOrderJson?: string;
  upiId?: string;
  address?: string;
  remindViaEmail?: boolean;
  remindViaWhatsApp?: boolean;
  siteUrl?: string;
}

// Cash Management Types
export interface SaveDailyCashRequest {
  date: string;
  openingCash: number;
  closingCash?: number | null;
}

export interface AddExpenseRequest {
  date: string;
  category: string;
  amount: number;
  notes?: string;
}

export interface DailyCashEntryDto {
  dailyCashEntryId: number;
  date: string;
  openingCash: number;
  closingCash: number | null;
  totalExpenses: number;
  netCashFlow: number;
  estimatedSales: number;
  expenses: DailyExpenseDto[];
}

export interface DailyExpenseDto {
  dailyExpenseId: number;
  date: string;
  category: string;
  amount: number;
  notes: string | null;
}

export interface MonthlyCashSummaryDto {
  month: string;
  firstOpeningCash: number;
  lastClosingCash: number;
  totalCashInflow: number;
  totalExpenses: number;
  estimatedProfit: number;
  daysRecorded: number;
  categoryBreakdown: CategoryExpenseDto[];
  dailyEntries: DailyCashEntryDto[];
}

export interface CategoryExpenseDto {
  category: string;
  total: number;
  count: number;
}

export interface TenantCashReportDto {
  tenantId: number;
  tenantName: string;
  month: string;
  totalCashInflow: number;
  totalExpenses: number;
  estimatedProfit: number;
  daysRecorded: number;
  categoryBreakdown: CategoryExpenseDto[];
}

export interface YearlyCashSummaryDto {
  year: string;
  totalCashInflow: number;
  totalExpenses: number;
  estimatedProfit: number;
  totalDaysRecorded: number;
  categoryBreakdown: CategoryExpenseDto[];
  monthlyBreakdown: MonthlyCashSummaryDto[];
}

export interface TenantYearlyCashReportDto {
  tenantId: number;
  tenantName: string;
  year: string;
  totalCashInflow: number;
  totalExpenses: number;
  estimatedProfit: number;
  totalDaysRecorded: number;
  categoryBreakdown: CategoryExpenseDto[];
  monthlyBreakdown: MonthlyCashSummaryDto[];
}

export interface ExpenseCategoryDto {
  expenseCategoryId: number;
  name: string;
  isDefault: boolean;
  sortOrder: number;
  isActive: boolean;
}

// Dashboard Metrics
export interface DashboardMetricsDto {
  activeSubscribers: number;
  totalDeliveriesToday: number;
  pendingDeliveriesToday: number;
  failedDeliveriesToday: number;
  todayQuantity: number;
  unbilledAmount: number;
  totalOutstanding: number;
  monthlyRevenue: number;
  monthlyCashInflow: number;
  monthlyExpenses: number;
  monthlyProfit: number;
  currentMonth: string;
  totalSubscribers: number;
  inactiveSubscribers: number;
  newSubscribersThisMonth: number;
  todaysCompletedDeliveries: number;
  deliverySuccessRatePercent: number;
  todaysCollections: number;
  lastMonthTotalBillAmount: number;
  lastMonthCollectedAmount: number;
  lastMonthPendingAmount: number;
}

export interface RevenueTrendPointDto {
  month: string;
  amount: number;
}

export interface PaymentMethodBreakdownDto {
  mode: string;
  amount: number;
  count: number;
}

export interface CashFlowDashboardDto {
  todayIncome: number;
  thisMonthIncome: number;
  thisMonthExpenses: number;
  netCashFlow: number;
  outstandingReceivables: number;
  collectionPercentage: number;
  monthlyRevenueTrend: RevenueTrendPointDto[];
  paymentMethodBreakdown: PaymentMethodBreakdownDto[];
}

// Cash Management Backup DTOs
export interface DailyCashEntryBackupDto {
  dailyCashEntryId: number;
  date: string;
  openingCash: number;
  closingCash: number | null;
}

export interface DailyExpenseBackupDto {
  dailyExpenseId: number;
  dailyCashEntryId: number;
  date: string;
  category: string;
  amount: number;
  notes: string | null;
}

export interface ExpenseCategoryBackupDto {
  expenseCategoryId: number;
  name: string;
  isDefault: boolean;
  sortOrder: number;
  isActive: boolean;
}

// AI Documents
export interface DocumentDto {
  name: string;
  type: string;
  size: number;
  uploadDate: string;
}

// ===== Record Store Types =====
export interface RecordFileDto {
  name: string;
  type: string;
  size: number;
  uploadDate: string;
  folder: string;
  tenantId: number;
}

export interface RecordStoreTenantDto {
  tenantId: number;
  tenantName: string;
  folder: string;
  fileCount: number;
}

// ===== Purchase/Sale Types =====
export interface PurchaseEntryDto {
  purchaseEntryId: number;
  tenantId: number;
  entryDate: string;
  vendorName: string;
  milkTypeId?: number;
  milkTypeName?: string;
  productName?: string;
  quantity: number;
  fat?: number;
  snf?: number;
  rate: number;
  amount: number;
  paymentStatus: string;
  remarks?: string;
  createdDate: string;
  updatedDate?: string;
}

export interface SaleEntryDto {
  saleEntryId: number;
  tenantId: number;
  entryDate: string;
  customerName: string;
  milkTypeId?: number;
  milkTypeName?: string;
  productName?: string;
  quantity: number;
  rate: number;
  amount: number;
  deliveryDate?: string;
  paymentStatus: string;
  remarks?: string;
  createdDate: string;
  updatedDate?: string;
}

export interface CreatePurchaseEntryRequest {
  entryDate: string;
  vendorName: string;
  milkTypeId?: number;
  productName?: string;
  quantity: number;
  fat?: number;
  snf?: number;
  rate: number;
  paymentStatus?: string;
  remarks?: string;
}

export interface UpdatePurchaseEntryRequest {
  entryDate?: string;
  vendorName?: string;
  milkTypeId?: number;
  productName?: string;
  quantity?: number;
  fat?: number;
  snf?: number;
  rate?: number;
  paymentStatus?: string;
  remarks?: string;
}

export interface CreateSaleEntryRequest {
  entryDate: string;
  customerName: string;
  milkTypeId?: number;
  productName?: string;
  quantity: number;
  rate: number;
  deliveryDate?: string;
  paymentStatus?: string;
  remarks?: string;
}

export interface UpdateSaleEntryRequest {
  entryDate?: string;
  customerName?: string;
  milkTypeId?: number;
  productName?: string;
  quantity?: number;
  rate?: number;
  deliveryDate?: string;
  paymentStatus?: string;
  remarks?: string;
}

export interface PurchaseSaleFilter {
  from?: string;
  to?: string;
  vendorOrCustomer?: string;
  milkTypeId?: number;
  paymentStatus?: string;
}

export interface PurchaseDailySummary { date: string; quantity: number; amount: number; count: number; }
export interface SaleDailySummary { date: string; quantity: number; amount: number; count: number; }
export interface VendorSummary { vendorName: string; totalQuantity: number; totalAmount: number; count: number; }
export interface CustomerSummary { customerName: string; totalQuantity: number; totalAmount: number; count: number; }
export interface PurchaseMonthSummary { month: string; quantity: number; amount: number; count: number; }
export interface SaleMonthSummary { month: string; quantity: number; amount: number; count: number; }

export interface PurchaseDailyReport {
  date: string;
  totalEntries: number;
  totalQuantity: number;
  totalAmount: number;
  entries: PurchaseEntryDto[];
}
export interface SaleDailyReport {
  date: string;
  totalEntries: number;
  totalQuantity: number;
  totalAmount: number;
  entries: SaleEntryDto[];
}
export interface PurchaseMonthlyReport {
  month: string;
  totalEntries: number;
  totalQuantity: number;
  totalAmount: number;
  dailyBreakdown: PurchaseDailySummary[];
  vendorBreakdown: VendorSummary[];
  productTypeBreakdown: ProductTypeBreakdownDto[];
}
export interface SaleMonthlyReport {
  month: string;
  totalEntries: number;
  totalQuantity: number;
  totalAmount: number;
  dailyBreakdown: SaleDailySummary[];
  customerBreakdown: CustomerSummary[];
  productTypeBreakdown: ProductTypeBreakdownDto[];
}
export interface PurchaseYearlyReport {
  year: number;
  totalQuantity: number;
  totalAmount: number;
  monthlyBreakdown: PurchaseMonthSummary[];
}
export interface SaleYearlyReport {
  year: number;
  totalQuantity: number;
  totalAmount: number;
  monthlyBreakdown: SaleMonthSummary[];
}
export interface PurchaseVsSaleSummary {
  period: string;
  totalPurchaseAmount: number;
  totalSaleAmount: number;
  profitLoss: number;
  totalPurchaseQuantity: number;
  totalSaleQuantity: number;
}
export interface PurchaseSaleDashboard {
  todayPurchaseAmount: number;
  todaySaleAmount: number;
  monthlyPurchaseAmount: number;
  monthlySaleAmount: number;
  totalPurchaseAmount: number;
  totalSaleAmount: number;
  monthlyProfitLoss: number;
  pendingPurchasePayments: number;
  pendingSalePayments: number;
}

// ===== Module Access Types =====
export interface AppModuleDto {
  appModuleId: number;
  moduleKey: string;
  displayName: string;
  description?: string;
  isActive: boolean;
  displayOrder: number;
}
export interface ModuleStatusDto {
  appModuleId: number;
  moduleKey: string;
  displayName: string;
  isEnabled: boolean;
}
export interface TenantModuleMatrix {
  tenantId: number;
  tenantName: string;
  modules: ModuleStatusDto[];
}
export interface TenantEnabledModules {
  tenantId: number;
  enabledModuleKeys: string[];
}

// ===== Product Type Types =====
export interface ProductTypeDto {
  productTypeId: number;
  tenantId: number;
  name: string;
  unit: string | null;
  description: string | null;
  isActive: boolean;
  displayOrder: number;
  createdDate: string;
  updatedDate: string | null;
  currentPurchaseRate: number | null;
  currentSaleRate: number | null;
}
export interface CreateProductTypeRequest {
  name: string;
  unit?: string;
  description?: string;
  displayOrder?: number;
  initialPurchaseRate?: number;
  initialSaleRate?: number;
  initialRateEffectiveDate?: string;
}
export interface UpdateProductTypeRequest {
  name?: string;
  unit?: string;
  description?: string;
  isActive?: boolean;
  displayOrder?: number;
}

export interface BulkCreateSkippedItem {
  name: string;
  reason: string;
}

export interface BulkCreateProductTypesResult {
  created: number;
  skipped: number;
  skippedItems: BulkCreateSkippedItem[];
  summary: string;
}

// ===== Product Type Rate History =====

export interface ProductTypeRateDto {
  productTypeRateId: number;
  productTypeId: number;
  effectiveDate: string;
  purchaseRate: number;
  saleRate: number;
  notes?: string;
  createdDate: string;
}

export interface CreateProductTypeRateRequest {
  effectiveDate: string;
  purchaseRate: number;
  saleRate: number;
  notes?: string;
}

export interface EffectiveRateDto {
  purchaseRate: number;
  saleRate: number;
  effectiveDate: string;
}

export interface ProductTypeBreakdownDto {
  productName: string;
  totalQuantity: number;
  totalAmount: number;
  count: number;
}

export interface DeliveryBoyDto {
  userId: number;
  tenantId: number;
  name: string;
  email: string;
  phone?: string;
  address?: string;
  isActive: boolean;
  assignedSocieties: string[];
}

export interface CreateDeliveryBoyRequest {
  name: string;
  email: string;
  password: string;
  phone?: string;
  address?: string;
  assignedSocieties: string[];
}

export interface UpdateDeliveryBoyRequest {
  name: string;
  email: string;
  phone?: string;
  address?: string;
}

// ===== Audit Log =====
export interface AuditLogDto {
  auditLogId: number;
  tenantId: number;
  tenantName?: string;
  userId?: number;
  userName?: string;
  action: string;
  entityType: string;
  entityId: number;
  oldValues?: string;
  newValues?: string;
  description?: string;
  actionType: string;
  ipAddress?: string;
  createdDate: string;
  isReverted: boolean;
  revertedAt?: string;
  revertReason?: string;
}

export interface AuditLogListResponse {
  logs: AuditLogDto[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface AuditLogFilters {
  entityType?: string;
  actionType?: string;
  action?: string;
  userId?: number;
  fromDate?: string;
  toDate?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface AuditLogStatsDto {
  totalActions: number;
  userActions: number;
  systemActions: number;
  revertedActions: number;
  actionsByType: Record<string, number>;
  actionsByEntity: Record<string, number>;
}

export interface RevertAuditRequest {
  reason?: string;
}

// ===== Tenant Request =====
export interface TenantRequestDto {
  tenantRequestId: number;
  dairyName: string;
  ownerName: string;
  mobile: string;
  email: string;
  city: string;
  subscriberCount: number;
  preferredPlan: string;
  message?: string;
  status: string;
  adminEmail?: string;
  adminPassword?: string;
  rejectionReason?: string;
  createdTenantId?: number;
  createdDate: string;
  reviewedDate?: string;
  reviewedByUserId?: string;
}

export interface CreateTenantRequestRequest {
  dairyName: string;
  ownerName: string;
  mobile: string;
  email: string;
  city: string;
  subscriberCount: number;
  preferredPlan: string;
  message?: string;
}

export interface ApproveTenantRequestRequest {
  adminEmail: string;
  adminPassword: string;
}

export interface RejectTenantRequestRequest {
  reason?: string;
}

export const api = new MilkApiClient();
export default api;
